type Left<T> = {
    left: NonNullable<T>;
    right?: never;
};

type Right<U> = {
    left?: never;
    right: NonNullable<U>;
};

export type Either<T, U> = Left<T> | Right<U>;

export function unwrap<T, _U>(left: Left<T>): T;
export function unwrap<_T, U>(right: Right<U>): U;
export function unwrap<T, U>(either: Either<T, U>): T | U {
    if (isLeft(either)) {
        return either.left;
    }

    if (isRight(either)) {
        return either.right;
    }

    throw new Error('[Either.unwrap] panic: recieved undefined left and right values for Either');
}

export const isLeft = <T, U>(either: Either<T, U>): either is Left<T> => either.left !== undefined;
export const isRight = <T, U>(either: Either<T, U>): either is Right<U> =>
    either.right !== undefined;

export const left = <T>(value: NonNullable<T>): Left<T> => ({ left: value });
export const right = <U>(value: NonNullable<U>): Right<U> => ({ right: value });

class Chain<L, R> {
    constructor(private either: Either<L, R>) {}

    bind<L2, R2>(eitherable: (right: R) => Either<L | L2, R2>) {
        if (isRight(this.either)) {
            const unwrapped = unwrap(this.either);
            return new Chain(eitherable(unwrapped));
        }

        return new Chain<L | L2, R2>(this.either);
    }

    onLeft(onError: (left: L) => void) {
        if (isLeft(this.either)) {
            onError(unwrap(this.either));
        }
        return this;
    }

    onRight(onSuccess: (right: R) => void) {
        if (isRight(this.either)) {
            onSuccess(unwrap(this.either));
        }
        return this;
    }

    concat<L2, R2>(either: Either<L2, R2>): Chain<L | L2, [R2, R]> {
        if (isLeft(this.either)) {
            return new Chain<L | L2, [R2, R]>(this.either);
        }

        if (isLeft(either)) {
            return new Chain<L | L2, [R2, R]>(either);
        }

        const head = unwrap(either);
        const tail = unwrap(this.either);

        return new Chain<L | L2, [R2, R]>(right([head, tail]));
    }

    lift<R2>(safeOperation: (r: R) => NonNullable<R2>): Chain<L, R2> {
        if (isLeft(this.either)) {
            return new Chain<L, R2>(this.either);
        }

        const unwrapped = unwrap(this.either);
        const safe = safeOperation(unwrapped);
        return new Chain<L, R2>(right(safe));
    }

    fail<L2>(handler: (failure: L) => NonNullable<L2>): Chain<L2, R> {
        if (isLeft(this.either)) {
            const failure = unwrap(this.either);
            const consolidated = handler(failure);
            return new Chain<L2, R>(left(consolidated));
        }

        return new Chain<L2, R>(this.either);
    }

    catch(handler: (failure: L) => Either<L, R>): Chain<L, R> {
        if (isLeft(this.either)) {
            const failure = unwrap(this.either);
            const handled = handler(failure);
            return new Chain<L, R>(handled);
        }
        return new Chain<L, R>(this.either);
    }

    mapLeft<L2>(map: (left: L) => NonNullable<L2>): Chain<L2, R> {
        if (isLeft(this.either)) {
            const mapped = map(unwrap(this.either));
            return new Chain<L2, R>(left(mapped));
        }
        return new Chain<L2, R>(this.either);
    }

    unwrap(): L | R {
        if (isLeft(this.either)) {
            return unwrap(this.either);
        }
        return unwrap(this.either);
    }

    get return() {
        return this.either;
    }
}

export const bind = <L, R>(either: Either<L, R>) => new Chain(either);
